<script setup lang="ts">
import "./MConsultationTemplateQuickActions.scss";
import type {
  MConsultationTemplateQuickActionsAction,
  MConsultationTemplateQuickActionsProps,
} from "./MConsultationTemplateQuickActions.props";
import { mConsultationTemplateQuickActionsDefaults } from "./MConsultationTemplateQuickActions.props";
import MButton from "../../MButton";
import { openModal } from "../../../../composables/dialog/drawer";
import axios from "../../../../utils/axios";
import { generateUUIDv7 } from "../../../../utils/helpers";

const props = withDefaults(
  defineProps<MConsultationTemplateQuickActionsProps>(),
  mConsultationTemplateQuickActionsDefaults,
);

// The create-modal route for each action type. Built on the frontend (like
// MInlineEntryList) so the buttons work from any flow that renders the template —
// create or edit — without depending on a backend-injected URL. `{patientId}` is
// interpolated below.
const QUICK_ACTION_ROUTES: Record<string, string> = {
  prescription: "/clinical/prescription/modal/create-prescription",
  immunisation: "/clinical/immunisation/new-immunisation",
  fitNote: "/clinical/gb/fit-note/create",
  referral: "/referrals/create-referral-method-selection",
  investigationRequest: "/clinical/investigation-request/record-manually-requested-investigations",
  document: "/clinical/document/new-document-modal",
  futureAction: "/future-action/create",
  communication: "/clinical/communication/create-care-record-communication",
};

// Records created via a quick action must attach to a real topic heading (not the
// bare topic) — the same rule every other template element already follows. Resolve
// the heading declared on this block (by its SNOMED code); create it just-in-time if
// it doesn't exist yet on this topic.
async function resolveHeadingId(): Promise<string | null> {
  if (!props.heading || !props.consultationTopicId) return null;

  const { data } = await axios.get<{ consultationTopicHeadingId: string | null }>(
    `/clinical/data/encounter/consultation-topic/heading/resolve-quick-action-heading/${props.consultationTopicId}/${props.heading}`,
  );

  if (data.consultationTopicHeadingId) return data.consultationTopicHeadingId;

  const consultationTopicHeadingId = generateUUIDv7();
  await axios.post("/clinical/encounter/consultation-topic/heading/resolve-quick-action-heading", {
    consultationTopicHeadingId,
    consultationTopicId: props.consultationTopicId,
    heading: props.heading,
  });

  return consultationTopicHeadingId;
}

type ModalTarget = {
  url: string;
  consultationTopicHeadingId: string;
};

// Needs the patient + consultation-topic-heading context to target the modal. Where
// any is absent (e.g. the read-only authoring preview) the button stays inert.
async function buildModalTarget(
  action: MConsultationTemplateQuickActionsAction,
): Promise<ModalTarget | null> {
  const base = QUICK_ACTION_ROUTES[action.type];
  if (!base || !props.patientId) return null;

  const consultationTopicHeadingId = await resolveHeadingId();
  if (!consultationTopicHeadingId) return null;

  const params = new URLSearchParams({
    contextId: consultationTopicHeadingId,
    contextType: "consultation-topic-heading",
  });

  if (action.drugCode) params.set("drugCode", action.drugCode);
  if (action.vaccineCode) params.set("vaccineCode", action.vaccineCode);
  if (action.conceptId) params.set("conceptId", action.conceptId);
  if (action.messageBody) params.set("messageBody", action.messageBody);

  return { url: `${base}/${props.patientId}?${params.toString()}`, consultationTopicHeadingId };
}

// The create endpoints behind these modals validate uuid (the new entity's id) plus
// sortOrder/sortOrderHash (an optimistic-concurrency token on the heading's entry
// ordering) whenever a consultation-topic-heading context is present — the same
// contract MInlineEntryList.handleActionSelected() satisfies from its own local
// entries/sortOrder state. This component has none, so it fetches the heading's
// current entries fresh right before opening the modal.
async function fetchSortOrderPayload(consultationTopicHeadingId: string) {
  const { data } = await axios.get<{
    entries: { id: string; type: string }[];
    sortOrderHash: string;
  }>(`/clinical/encounter/consultation-topic/topic-heading-entries/${consultationTopicHeadingId}`);

  return {
    sortOrder: data.entries.map((entry) => ({ id: entry.id, entryType: entry.type })),
    sortOrderHash: data.sortOrderHash,
  };
}

// Guards against rapid double-clicks stacking multiple modals: while one open is
// in flight (including the heading resolve/create round-trip) we ignore further
// clicks, releasing once the open promise settles.
let opening = false;

async function handleActionClick(action: MConsultationTemplateQuickActionsAction): Promise<void> {
  if (opening) return;

  opening = true;
  try {
    const target = await buildModalTarget(action);
    if (!target) return;

    const { sortOrder, sortOrderHash } = await fetchSortOrderPayload(
      target.consultationTopicHeadingId,
    );

    await openModal(target.url, {
      patientId: props.patientId,
      allowDockedModal: true,
      additionalCreatePayloadData: {
        uuid: generateUUIDv7(),
        sortOrder,
        sortOrderHash,
      },
    });
  } finally {
    opening = false;
  }
}
</script>

<template>
  <div class="m-consultation-template-quick-actions">
    <h4 class="m-consultation-template-quick-actions__label">{{ label }}</h4>
    <div class="m-consultation-template-quick-actions__buttons">
      <MButton
        v-for="action in actions"
        :key="action.name"
        :label="action.label ?? action.type"
        color="secondary"
        @click="handleActionClick(action)"
      />
    </div>
  </div>
</template>
